import * as Rx from 'rxjs';
import { filter, map, mergeAll } from 'rxjs/operators';
import { Collections } from '../../../collections.model';
import { RequestDataPayload } from '../../actions';
import { distinctInWindow } from '../../operators/distinctInWindow';
import { referencesEqual } from '../../store.helpers';

/**
 * Deduplicates requests by comparing them with requests received in the last
 * `windowTime` ms.
 *
 * Requests with ids: (e.g. #objects and #object)
 * If a request asks for a particular object id which has been requested before
 * within the interval, it is removed from the request, and then emitted.
 * If this would result in no object ids remaining, the original request
 * is not emitted. Note that the original request is split such that each
 * output request only contains one id each, all emitted simultaneously.
 * i.e. U1:2:3 => U1,U2,U3
 *
 * Example: With a window size of 3 seconds, a request for user with id 1 (U1),
 * then for ids 2 and 1 (U2:1) two seconds later, then again two seconds later:
 *
 * 0     1     2     3     4     5s
 * U1----------U2:1--------U2:1--|
 *        dedupeRequests(3000)
 * U1----------U2----------U1----|
 *
 * Please visit the `.spec.ts` file for more details + diagrams
 *
 * Requests with queries: (e.g. #list)
 * If a request has the same query as another request within the interval,
 * it is not emitted, including empty query objects.
 *
 * @param windowTime window interval in milliseconds
 */
export function dedupeRequests<K extends keyof Collections>(
  windowTime: number,
  requestErrors$: Rx.Observable<RequestDataPayload<K>>,
  scheduler: Rx.SchedulerLike = Rx.asyncScheduler,
): (
  source$: Rx.Observable<RequestDataPayload<K>>,
) => Rx.Observable<RequestDataPayload<K>> {
  if (windowTime === 0) {
    return (source$: Rx.Observable<RequestDataPayload<K>>) => source$;
  }

  const compareFn = (x: RequestDataPayload<K>, y: RequestDataPayload<K>) =>
    referencesEqual(x.ref, y.ref);

  // contains the list of the requests that did error during the current window
  // FIXME: remove subscribe? how?
  const requestErrorsBuffer: RequestDataPayload<K>[] = [];
  requestErrors$.subscribe(x => {
    if (!requestErrorsBuffer.some(v => compareFn(x, v))) {
      requestErrorsBuffer.push(x);
      scheduler.schedule(() => requestErrorsBuffer.shift(), windowTime);
    }
  });

  const dedupeRequestsErrors = (
    source$: Rx.Observable<RequestDataPayload<K>>,
  ) =>
    source$.pipe(
      filter(request => {
        const previousError = requestErrorsBuffer.findIndex(r =>
          referencesEqual(r.ref, request.ref),
        );
        if (previousError >= 0) {
          requestErrorsBuffer.splice(previousError, 1);
          return true;
        }
        return false;
      }),
    );

  return (source$: Rx.Observable<RequestDataPayload<K>>) =>
    Rx.merge(
      source$.pipe(
        map(req => splitOnIds(req)), // all requests split into ONE id each
        mergeAll(),
        distinctInWindow(windowTime, compareFn, scheduler),
      ),
      dedupeRequestsErrors(source$),
    );
}

/**
 * Splits a single request with more than one object id into multiple requests
 * each with one id in its path
 */
function splitOnIds<K extends keyof Collections>(
  request: RequestDataPayload<K>,
): ReadonlyArray<RequestDataPayload<K>> {
  const { path } = request.ref;
  const objectIds = path.ids || [];

  if (objectIds.length === 0) {
    return [request];
  }

  return objectIds.reduce((requests: RequestDataPayload<K>[], id) => {
    const splitReq: RequestDataPayload<K> = {
      type: path.collection,
      ref: {
        path: {
          collection: path.collection,
          authUid: path.authUid,
          ids: [id],
        },
      },
      clientRequestIds: request.clientRequestIds,
    };
    return [...requests, splitReq];
  }, []);
}
